import React, { useEffect, useId, useRef } from "react";
import { AlertTriangle, ArrowRight, Check, Clock, LoaderCircle } from "lucide-react";
import studyDenHorizontal from "../assets/images/branding/Studyden_Horizontal.svg";
import "../EnterQuizAssessment.css";
import "./AssessmentOutcome.css";

// What the student is told after the timer reaches zero depends only on what
// the server confirmed: "pending" while the expiry submission is in flight,
// "expired" once it was accepted, "already-submitted" when the attempt had
// been submitted before, "unconfirmed" when this device could not confirm it.
const TIME_UP_STATES = {
  pending: {
    tone: "info",
    message: "Submitting the answers StudyDen saved before the time limit…",
    detail: null,
  },
  expired: {
    tone: "info",
    message: "The answers StudyDen saved before the time limit have been submitted for marking.",
    detail: "Anything entered after the time limit isn't included.",
  },
  "already-submitted": {
    tone: "success",
    message: "This assessment had already been submitted before the time limit.",
    detail: null,
  },
  unconfirmed: {
    tone: "warning",
    message: "StudyDen couldn't confirm the submission from this device.",
    detail: "Answers saved before the time limit are kept and will be submitted automatically. Anything entered after the time limit isn't included.",
  },
};

function OutcomeIcon({ kind, tone, pending }) {
  if (pending) return <LoaderCircle className="eq-outcome-spin" size={30} />;
  if (tone === "warning") return <AlertTriangle size={30} />;
  if (kind === "time-up" && tone !== "success") return <Clock size={30} />;
  return <Check size={34} strokeWidth={3} />;
}

// Full-page result of finishing an assessment. The redirect itself is owned
// by EnterQuiz; this shows its countdown and an explicit way to go now.
export default function AssessmentOutcome({ kind, status = "pending", secondsRemaining, totalSeconds, destinationLabel, onLeave, darkMode }) {
  const headingId = useId();
  const headingRef = useRef(null);
  useEffect(() => { headingRef.current?.focus(); }, []);

  const timeUp = kind === "time-up";
  const state = timeUp ? (TIME_UP_STATES[status] || TIME_UP_STATES.pending) : {
    tone: "success",
    message: "StudyDen has received your answers.",
    detail: "Your submission is saved and ready for marking.",
  };
  const seconds = Math.max(0, Number(secondsRemaining) || 0);

  return (
    <div className={`eq-assess-page eq-outcome-page${darkMode ? " eq-assess-dark" : ""}`}>
      <main className="eq-outcome-main">
        <section className={`eq-outcome-card is-${state.tone}`} aria-labelledby={headingId}>
          <img className="eq-outcome-logo" src={studyDenHorizontal} alt="StudyDen" />
          <div className="eq-outcome-icon" aria-hidden="true">
            <OutcomeIcon kind={kind} tone={state.tone} pending={timeUp && status === "pending"} />
          </div>
          <h1 id={headingId} ref={headingRef} tabIndex={-1}>{timeUp ? "Time's up" : "Assessment submitted"}</h1>
          <div className="eq-outcome-message" role="status" aria-live="polite">
            <p>{state.message}</p>
            {state.detail && <p className="eq-outcome-detail">{state.detail}</p>}
          </div>
          <div className="eq-outcome-redirect">
            <p>
              {seconds > 0
                ? `Taking you to ${destinationLabel} in ${seconds} ${seconds === 1 ? "second" : "seconds"}.`
                : `Taking you to ${destinationLabel} now…`}
            </p>
            <div className="eq-outcome-progress" aria-hidden="true">
              <span style={{ animationDuration: `${totalSeconds}s` }} />
            </div>
          </div>
          <button type="button" className="eq-outcome-button" onClick={onLeave}>
            Return to {destinationLabel}
            <ArrowRight size={18} aria-hidden="true" />
          </button>
        </section>
      </main>
    </div>
  );
}
